import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { resolveCrmProfileUsername } from '@/shared/auth/auth.reducer';
import { API_ROUTES } from '@/config/api-routes';
import { readDownloadFilename } from '../utils/download-filename';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
import { appendCriteriaContains, appendCriteriaEquals, appendPageable, readPagedRows, } from '../utils/spring-api';
const kasesUrl = API_ROUTES.kases;
const assignmentBase = API_ROUTES.caseAssignments;
const initialState = {
    loading: false,
    errorMessage: null,
    cases: [],
    totalItems: 0,
    reference: null,
    referenceLoading: false,
    assignments: [],
    assignmentsLoading: false,
    caseDetail: null,
    detailLoading: false,
    statusHistory: null,
    statusHistoryLoading: false,
    savingFollowUp: false,
    savingAssignmentId: null,
    reopening: false,
};
export const getFollowUpReference = createAsyncThunk(`${kasesUrl}/reference`, async () => {
    const issuesRes = await axios.get(`${API_ROUTES.issues}?page=0&size=1000`);
    return {
        data: {
            issues: Array.isArray(issuesRes.data) ? issuesRes.data : issuesRes.data?.content ?? [],
            provinces: [],
            departments: [],
            districts: [],
        },
    };
}, { serializeError: serializeAxiosError });
export const getFollowUpCases = createAsyncThunk(`${kasesUrl}/follow-up`, async (params, { getState }) => {
    const auth = getState().auth;
    const username = resolveCrmProfileUsername(auth.crmProfile) ?? auth.session?.username;
    if (!username) {
        return { data: [], headers: { 'x-total-count': '0' } };
    }
    const q = new URLSearchParams();
    appendPageable(q, params.page, params.size, params.sort);
    if (params.tab === 'assigned') {
        appendCriteriaEquals(q, 'followUp', true);
        appendCriteriaEquals(q, 'assignedAgentUsername', username);
    }
    else {
        appendCriteriaEquals(q, 'userUsername', username);
    }
    appendCriteriaContains(q, 'phoneNumber', params.phoneNumber);
    appendCriteriaContains(q, 'caseNumber', params.caseNumber);
    appendCriteriaEquals(q, 'provinceId', params.provinceId);
    appendCriteriaEquals(q, 'districtId', params.districtId);
    appendCriteriaEquals(q, 'issueId', params.issueId);
    appendCriteriaEquals(q, 'assignedDepartmentId', params.departmentId);
    appendCriteriaEquals(q, 'status', params.status);
    appendCriteriaEquals(q, 'startDate', params.startDate);
    appendCriteriaEquals(q, 'endDate', params.endDate);
    const res = await axios.get(`${kasesUrl}?${q.toString()}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { data: rows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getFollowUpAssignments = createAsyncThunk('followUpCases/fetch_assignments', async (caseId) => {
    const res = await axios.get(`${assignmentBase}?${assignmentListQuery({ kaseId: caseId, page: 0, size: 1000, active: true })}`);
    const { rows } = readPagedRows(res.data, res.headers);
    return { data: rows };
}, { serializeError: serializeAxiosError });
export const getFollowUpCaseDetail = createAsyncThunk('followUpCases/fetch_detail', async (caseId) => {
    const [kaseRes, assignmentsRes, statusHistoryRes] = await Promise.all([
        axios.get(`${kasesUrl}/${caseId}`),
        axios.get(`${assignmentBase}?${assignmentListQuery({ kaseId: caseId, page: 0, size: 1000, active: true })}`),
        axios
            .get(API_ROUTES.kaseStatusHistory(caseId))
            .catch(() => ({ data: null })),
    ]);
    const { rows } = readPagedRows(assignmentsRes.data, assignmentsRes.headers);
    return {
        caseDetail: kaseRes.data,
        assignments: rows,
        statusHistory: statusHistoryRes.data,
    };
}, { serializeError: serializeAxiosError });
/** Includes inactive assignments — used when reopening a closed case. */
export const getFollowUpCaseAssignmentsForReopen = createAsyncThunk('followUpCases/fetch_assignments_for_reopen', async (caseId) => {
    const res = await axios.get(`${assignmentBase}?${assignmentListQuery({ kaseId: caseId, page: 0, size: 1000 })}`);
    const { rows } = readPagedRows(res.data, res.headers);
    return { data: rows };
}, { serializeError: serializeAxiosError });
function assignmentListQuery({ page, size, sort, kaseId, active, }) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'kaseId', kaseId);
    appendCriteriaEquals(q, 'active', active);
    return q.toString();
}
function buildFollowUpCasePatchBody(payload) {
    const body = {
        id: Number(payload.caseId),
    };
    if (payload.status !== undefined)
        body.status = payload.status;
    if (payload.statusDescription !== undefined)
        body.statusDescription = payload.statusDescription;
    body.followUpDate = new Date().toISOString();
    body.followUp = true;
    return body;
}
function buildFollowUpAssignmentPatchBody(payload) {
    const body = {
        id: Number(payload.assignmentId),
        kaseId: Number(payload.caseId),
    };
    if (payload.status !== undefined)
        body.status = payload.status;
    if (payload.progressPercentage !== undefined)
        body.progressPercentage = payload.progressPercentage;
    if (payload.weightPercentage !== undefined)
        body.weightPercentage = payload.weightPercentage;
    if (payload.assignmentNotes !== undefined)
        body.assignmentNotes = payload.assignmentNotes;
    if (payload.completionNotes !== undefined)
        body.completionNotes = payload.completionNotes;
    if (payload.role !== undefined)
        body.role = payload.role;
    if (payload.active !== undefined)
        body.active = payload.active;
    if (payload.isClosed !== undefined)
        body.isClosed = payload.isClosed;
    return body;
}
export const saveFollowUpCase = createAsyncThunk('followUpCases/save_follow_up', async (payload) => axios.patch(`${kasesUrl}/${payload.caseId}`, buildFollowUpCasePatchBody(payload)), { serializeError: serializeAxiosError });
export const patchFollowUpAssignment = createAsyncThunk('followUpCases/patch_assignment', async (payload) => axios.patch(`${assignmentBase}/${payload.assignmentId}`, buildFollowUpAssignmentPatchBody(payload)), { serializeError: serializeAxiosError });
export const reopenFollowUpCaseAssignments = createAsyncThunk('followUpCases/reopen_assignments', async (payload) => {
    const res = await axios.post(API_ROUTES.kaseReopenAssignments(payload.caseId), {
        assignmentIds: payload.assignmentIds.map((id) => Number(id)),
        reason: payload.reason.trim(),
    });
    return res.data;
}, { serializeError: serializeAxiosError });
export const downloadKaseAttachment = createAsyncThunk('followUpCases/download_attachment', async ({ caseId, attachmentId = 0, fallbackName, }) => {
    const response = await axios.get(`${kasesUrl}/${caseId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
    return { blob: response.data, filename: readDownloadFilename(response, fallbackName) };
}, { serializeError: serializeAxiosError });
export const followUpCasesSlice = createSlice({
    name: 'followUpCases',
    initialState: initialState,
    reducers: {
        clearFollowUpAssignments(state) {
            state.assignments = [];
        },
        clearFollowUpCaseDetail(state) {
            state.caseDetail = null;
            state.assignments = [];
            state.statusHistory = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getFollowUpReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload.data;
        })
            .addCase(getFollowUpCases.fulfilled, (state, action) => {
            state.loading = false;
            state.cases = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getFollowUpAssignments.fulfilled, (state, action) => {
            state.assignmentsLoading = false;
            state.assignments = action.payload.data;
        })
            .addCase(getFollowUpCaseDetail.fulfilled, (state, action) => {
            state.detailLoading = false;
            state.statusHistoryLoading = false;
            state.caseDetail = action.payload.caseDetail;
            state.assignments = action.payload.assignments;
            state.statusHistory = action.payload.statusHistory;
        })
            .addCase(getFollowUpCaseAssignmentsForReopen.fulfilled, (state, action) => {
            state.assignmentsLoading = false;
            state.assignments = action.payload.data;
        })
            .addMatcher(isPending(getFollowUpReference), (state) => {
            state.referenceLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getFollowUpCases), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getFollowUpAssignments, getFollowUpCaseAssignmentsForReopen), (state) => {
            state.assignmentsLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getFollowUpCaseDetail), (state) => {
            state.detailLoading = true;
            state.statusHistoryLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(reopenFollowUpCaseAssignments), (state) => {
            state.reopening = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(saveFollowUpCase), (state) => {
            state.savingFollowUp = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(patchFollowUpAssignment), (state, action) => {
            state.savingAssignmentId = action.meta.arg.assignmentId;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getFollowUpReference), (state, action) => {
            state.referenceLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getFollowUpCases), (state, action) => {
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getFollowUpAssignments, getFollowUpCaseAssignmentsForReopen), (state, action) => {
            state.assignmentsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getFollowUpCaseDetail), (state, action) => {
            state.detailLoading = false;
            state.statusHistoryLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(reopenFollowUpCaseAssignments), (state, action) => {
            state.reopening = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(saveFollowUpCase, patchFollowUpAssignment), (state, action) => {
            state.savingFollowUp = false;
            state.savingAssignmentId = null;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher((action) => saveFollowUpCase.fulfilled.match(action) ||
            patchFollowUpAssignment.fulfilled.match(action) ||
            reopenFollowUpCaseAssignments.fulfilled.match(action), (state) => {
            state.savingFollowUp = false;
            state.savingAssignmentId = null;
            state.reopening = false;
        });
    },
});
export const { clearFollowUpAssignments, clearFollowUpCaseDetail } = followUpCasesSlice.actions;
export default followUpCasesSlice.reducer;
